import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import {
  Button,
  ButtonBase,
  CircularProgress,
  Icon,
  IconButton,
  Tooltip,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import HttpHandler, { getModelChangeEvent } from 'app/reusable/HttpHandler'
import { TOAST_FEEDBACK } from 'app/reusable/utils'
import RecordFormModal from 'app/components/RecordFormModal/RecordFormModal'
import { UNCATEGORIZED } from './modalityCategory'

const APP = 'activities'
export const CATEGORY_MODEL = 'activity-modality-category'

/**
 * Every modality category, by description. Reloads whenever one is
 * created, edited or deleted anywhere in the app (model-change event).
 */
export const useModalityCategories = () => {
  const [categories, setCategories] = useState(null)

  const load = useCallback(
    () =>
      HttpHandler.list(APP, CATEGORY_MODEL, {
        paginate_by: 100,
        order_by: 'description',
      })
        .then(({ data }) => setCategories(data?.data || []))
        .catch(() => {
          setCategories([])
          toast.error('Erro ao buscar as categorias de modalidade.')
        }),
    []
  )

  useEffect(() => {
    load()
    const { type } = getModelChangeEvent(APP, CATEGORY_MODEL)
    window.addEventListener(type, load)
    return () => window.removeEventListener(type, load)
  }, [load])

  return categories
}

const useStyles = makeStyles(({ palette }) => ({
  selector: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  selectorMobile: {
    flexWrap: 'nowrap',
    overflowX: 'auto',
    margin: '0 -16px',
    padding: '2px 16px',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  option: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 36,
    padding: '6px 14px',
    borderRadius: 18,
    fontSize: 14,
    fontWeight: 500,
    border: `1px solid ${palette.divider}`,
    backgroundColor: palette.background.paper,
    color: palette.text.primary,
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: palette.action.hover },
  },
  optionSelected: {
    borderColor: palette.primary.main,
    backgroundColor: palette.primary.main,
    color: palette.primary.contrastText,
    '&:hover': { backgroundColor: palette.primary.dark },
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
    boxShadow: `0 0 0 2px ${palette.background.paper}`,
  },
  managerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 0',
    borderBottom: `1px solid ${palette.divider}`,
  },
  managerName: { flex: 1, minWidth: 0, fontWeight: 500 },
  muted: { color: palette.text.secondary },
}))

export const CategoryDot = ({ color }) => {
  const classes = useStyles()
  if (!color) return null
  return (
    <span
      aria-hidden
      className={classes.dot}
      style={{ backgroundColor: color }}
    />
  )
}

/**
 * The page-wide category switch: "Todas", one option per category and
 * "Sem categoria". `value` is a category id (as a string),
 * `UNCATEGORIZED`, or `undefined` for all.
 */
export const CategorySelector = ({ categories, value, onChange, isMobile }) => {
  const classes = useStyles()

  const options = [
    { value: undefined, label: 'Todas' },
    ...categories.map((category) => ({
      value: String(category.id),
      label: category.description,
      color: category.color,
    })),
    { value: UNCATEGORIZED, label: 'Sem categoria' },
  ]

  return (
    <div
      role="radiogroup"
      aria-label="Categoria de modalidade"
      className={clsx(classes.selector, isMobile && classes.selectorMobile)}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <ButtonBase
            key={option.value || 'all'}
            role="radio"
            aria-checked={selected}
            className={clsx(classes.option, selected && classes.optionSelected)}
            onClick={() => onChange(option.value)}
          >
            <CategoryDot color={option.color} />
            {option.label}
          </ButtonBase>
        )
      })}
    </div>
  )
}

const CategoryRow = ({ category, canChange, canDelete, onEdit }) => {
  const classes = useStyles()
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const remove = () => {
    setDeleting(true)
    toast
      .promise(
        HttpHandler.delete(APP, CATEGORY_MODEL, category.id),
        TOAST_FEEDBACK.DELETE
      )
      .then(() => {
        window.dispatchEvent(getModelChangeEvent(APP, CATEGORY_MODEL))
        // Its modalities lose the category (`on_delete=SET_NULL`).
        window.dispatchEvent(getModelChangeEvent(APP, 'activity-modality'))
      })
      .catch(() => setDeleting(false))
  }

  return (
    <li className={classes.managerRow}>
      <CategoryDot color={category.color} />
      <span className={classes.managerName}>{category.description}</span>
      {confirming ? (
        <>
          <span className={classes.muted}>Excluir?</span>
          <Button
            size="small"
            color="primary"
            disabled={deleting}
            onClick={remove}
          >
            {deleting ? <CircularProgress size={16} /> : 'Sim'}
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => setConfirming(false)}
          >
            Não
          </Button>
        </>
      ) : (
        <>
          {canChange && (
            <Tooltip title="Editar">
              <IconButton
                size="small"
                aria-label={`Editar ${category.description}`}
                onClick={() => onEdit(category.id)}
              >
                <Icon fontSize="small">edit</Icon>
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Excluir">
              <IconButton
                size="small"
                aria-label={`Excluir ${category.description}`}
                onClick={() => setConfirming(true)}
              >
                <Icon fontSize="small">delete</Icon>
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
    </li>
  )
}

/**
 * Modal content for creating, renaming, recoloring and deleting modality
 * categories. Deleting one leaves its modalities without a category.
 */
export const ModalityCategoriesManager = ({ canAdd, canChange, canDelete }) => {
  const classes = useStyles()
  const categories = useModalityCategories()
  // `null`: the list; `'new'`: creating; an id: editing that category.
  const [editing, setEditing] = useState(null)

  if (editing)
    return (
      <>
        <Button
          size="small"
          className="mb-4"
          startIcon={<Icon>arrow_back</Icon>}
          onClick={() => setEditing(null)}
        >
          Voltar para as categorias
        </Button>
        <RecordFormModal
          djangoApp={APP}
          djangoModel={CATEGORY_MODEL}
          id={editing === 'new' ? undefined : editing}
          onSuccess={() => setEditing(null)}
        />
      </>
    )

  return (
    <>
      <p className={clsx('mt-0', classes.muted)}>
        Agrupe as modalidades em categorias para ver indicadores, turmas e
        chamadas de uma área por vez.
      </p>
      {categories === null ? (
        <div className="text-center py-4">
          <CircularProgress size={24} />
        </div>
      ) : categories.length ? (
        <ul className="m-0 p-0 mb-4" style={{ listStyle: 'none' }}>
          {categories.map((category) => (
            <CategoryRow
              key={category.id}
              category={category}
              canChange={canChange}
              canDelete={canDelete}
              onEdit={setEditing}
            />
          ))}
        </ul>
      ) : (
        <p className={clsx('mb-4', classes.muted)}>
          Nenhuma categoria cadastrada ainda.
        </p>
      )}
      {canAdd && (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={<Icon>add</Icon>}
          onClick={() => setEditing('new')}
        >
          Nova categoria
        </Button>
      )}
    </>
  )
}
